import React, { useMemo } from 'react'
import log from 'loglevel'
import { Dropdown } from 'react-bootstrap'
import { E2E_IDS } from 'src/Utils/testIds'
import { RISK_CATEGORY } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  StyledDropdownMenu,
  StyledDropdownToggle,
} from 'src/components/Reusable/StandardDropdown/StandardDropdown.styles'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useHasRiskCategoryConfiguration } from 'src/context/Gatekeeper'
import { useCreateOrUpdateRisks, useRisk } from '../risks.hooks'
import { RISK_CATEGORY_DROPDOWN, RISK_CATEGORY_LABEL } from '../risks.constants'
import { RiskLinkageExclamationIcon } from '../risks.styles'
import { useRisksContext } from '../context/RisksContext'

export const RisksCategoryMenu = ({
  riskId,
}: {
  riskId: string
}): JSX.Element => {
  const createOrUpdateRisk = useCreateOrUpdateRisks()
  const risk = useRisk(riskId)
  const riskObj = risk instanceof Risk ? risk.toObject() : undefined
  const canUpdateCategory = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISK_CATEGORIES,
    riskObj,
  )
  const useRiskCategoryConfiguration = useHasRiskCategoryConfiguration()
  const { categoryOptions } = useRisksContext()
  const riskCategoryOptionLabel = useMemo(() => {
    if (!categoryOptions || categoryOptions.length === 0) return {}

    return categoryOptions.reduce<Record<number, string>>((acc, option) => {
      acc[option.value] = option.name
      return acc
    }, {})
  }, [categoryOptions])

  if (!risk || !riskObj) {
    return <></>
  }

  const isNoCategory = risk.getCategory() === RISK_CATEGORY.NO_CATEGORY
  const category = risk.getCategory()
  const updateRisk = async (newCategory: number): Promise<void> => {
    try {
      risk.setCategory(newCategory)
      await createOrUpdateRisk([risk])
    } catch (err) {
      log.error(`Failed to update risk category for riskId: ${riskId}:`, err)
    }
  }

  return (
    <Dropdown
      id={E2E_IDS.RISK_CATEGORY_MENU}
      data-testid={E2E_IDS.RISK_CATEGORY_MENU}
      className="risk-category-menu"
    >
      <Dropdown.Toggle
        as={StyledDropdownToggle}
        width="100%"
        disabled={!canUpdateCategory}
      >
        {isNoCategory && <RiskLinkageExclamationIcon />}
        {useRiskCategoryConfiguration
          ? riskCategoryOptionLabel[category]
          : RISK_CATEGORY_LABEL[category]}
      </Dropdown.Toggle>
      <Dropdown.Menu
        as={StyledDropdownMenu}
        width="100%"
        disabled={!canUpdateCategory}
      >
        {useRiskCategoryConfiguration
          ? categoryOptions.map((item) => (
              <Dropdown.Item
                key={item.value}
                eventKey={item.value}
                onClick={() => updateRisk(item.value)}
              >
                {item.name}
              </Dropdown.Item>
            ))
          : RISK_CATEGORY_DROPDOWN.map((item) => (
              <Dropdown.Item
                key={item.name}
                eventKey={item.value}
                onClick={() => updateRisk(item.value)}
              >
                {item.name}
              </Dropdown.Item>
            ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}
